// File: components/TextComponent.tsx
import { useRef, useEffect } from "react";
import { Box, Paper, Typography } from "@mui/material";
import TiptapEditor from "./TiptapEditor";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import { Stack } from "@mui/system";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import TabletOutlinedIcon from "@mui/icons-material/TabletOutlined";
import SmartphoneIcon from "@mui/icons-material/Smartphone";

const TextComponent = ({
  component,
  isSelected,
  onSelect,
  onUpdate,
  theme,
  pageDictionary,
  setIsTextEditing,
  isTextEditing,
  setUnsavedText,
}: any) => {
  const fontColor = theme === "black" ? "#ffffff" : "#000000";
  const lang = JSON.parse(pageDictionary)?.settings?.lang;

  const handleClick = () => {
    if (!isSelected) {
      onSelect(component.id);
    }
  };

  const handleSave = (html: string) => {
    // Remove wrapping <section> tags
    let cleanHtml = html.replace(/^<section class="main">|<\/section>$/g, "");

    // Convert empty <p></p> to <p>&nbsp;</p> so they remain visible
    cleanHtml = cleanHtml.replace(/<p><\/p>/g, "<p>&nbsp;</p>");

    onUpdate(component.id, { text: cleanHtml });
    setIsTextEditing(null);
  };

  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isTextEditing || !editorRef.current) return;

    // try to focus a contenteditable inside the editor (Tiptap uses contenteditable)
    const editable = editorRef.current.querySelector(
      '[contenteditable="true"]'
    ) as HTMLElement | null;
    if (editable) {
      // put caret at end
      editable.focus();
      const range = document.createRange();
      range.selectNodeContents(editable);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    } else {
      // fallback: focus wrapper
      (editorRef.current as HTMLElement).focus();
    }
  }, [isTextEditing]);

  return (
    <Paper
      elevation={isSelected ? 4 : 1}
      component="section"
      className="main"
      tabIndex={-1}
      sx={{
        position: "relative",
        border: isSelected ? "2px solid #9C27B080" : "1px solid #e0e0e0",
        borderRadius: 1,
        padding: "8px 16px", //need to change here
        marginBottom: "24px",
        backgroundColor: theme === "black" ? "#000000" : "#ffffff",
        color: fontColor,
        boxShadow: `
      0px 2px 1px -1px #9C27B033,
      0px 1px 1px 0px #9C27B024,
      0px 1px 3px 0px #9C27B01F
    `,
      }}
      onClick={handleClick}
    >
      <Stack flexDirection={"row"} justifyContent={"space-between"} mb={2}>
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ color: "#000000DE" }}
        >
          <TextFieldsIcon sx={{ width: 16, height: 16, color: fontColor }} />
          <Typography sx={{ fontWeight: 500, color: fontColor }}>
            Content Block
          </Typography>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ color: fontColor }}
        >
          <Typography sx={{ fontWeight: 500 }}>Available On:</Typography>
          <TabletOutlinedIcon fontSize="small" />
          <DesktopWindowsOutlinedIcon fontSize="small" />
          <SmartphoneIcon fontSize="small" />
        </Stack>
      </Stack>

      {isTextEditing ? (
        <Box
          ref={editorRef}
          onClick={(e) => e.stopPropagation()}
          sx={{
            cursor: "text",
            backgroundColor: "white",
            color: "#1f2937",
            borderRadius: 2, // subtle rounded corners
            padding: 2, // comfortable spacing
            boxShadow: "0 2px 6px rgba(0,0,0,0.06)", // gentle shadow for depth
            marginBottom: "6px", // consistent spacing
          }}
        >
          <TiptapEditor
            initialContent={component.props.text || "<p></p>"}
            onSave={handleSave}
            lang={lang}
            editorProps={{
              style: {
                minHeight: "200px",
                width: "100%",
              },
            }}
            enableShortcodes={true}
            useDictionaryHeaders={true}
            component={component}
            pageDictionary={pageDictionary}
            setUnsavedText={setUnsavedText}
            onTempChange={(newHtml: string) => setUnsavedText(newHtml)}
          />
        </Box>
      ) : (
        <Box
          dangerouslySetInnerHTML={{
            __html: component.props.text || "<p></p>",
          }}
          mt={2}
          pb={1}
          mb={0.5}
        />
      )}
    </Paper>
  );
};

export default TextComponent;
